// Sound Map — a hand-authored phonetics cheat sheet, styled like a note jotted on
// paper. Every letter/digraph → plain-English sound → IPA → an example you can
// hear. Hand-authored on purpose: sound mapping is exactly the thing an AI image
// generator gets wrong.
import { speakButtonHTML } from "../lib/audio.js";

const GROUPS = [
  {
    title: "Vowels — pure & constant",
    note: "Five clean sounds that never change. Master these first.",
    color: "text-emerald-700",
    rows: [
      ["a", "“ah” as in father", "/a/", "casa"],
      ["e", "“eh” as in bed", "/e/", "mesa"],
      ["i", "“ee” as in see", "/i/", "sí"],
      ["o", "“oh” as in or", "/o/", "sol"],
      ["u", "“oo” as in food", "/u/", "luna"],
    ],
  },
  {
    title: "Consonants unlike English",
    note: "Where beginners slip — the rest sound close to English.",
    color: "text-amber-700",
    rows: [
      ["c + e/i", "soft “s” (or “th” in Spain)", "/s/ /θ/", "cena"],
      ["c + a/o/u", "hard “k”", "/k/", "casa"],
      ["g + e/i", "harsh “h”, like loch", "/x/", "gente"],
      ["g + a/o/u", "hard “g” as in go", "/ɡ/", "gato"],
      ["h", "silent — never pronounced", "—", "hola"],
      ["j", "harsh “h”, like loch", "/x/", "jamón"],
      ["r", "tapped; rolled to start a word", "/ɾ/ /r/", "rojo"],
      ["v", "pronounced just like “b”", "/b/", "vino"],
      ["z", "“s” (or “th” in Spain)", "/s/ /θ/", "zapato"],
    ],
  },
  {
    title: "Digraphs & special letters",
    note: "Two letters (or a tilde) making one sound of their own.",
    color: "text-sky-700",
    rows: [
      ["ch", "“ch” as in chair", "/tʃ/", "chico"],
      ["ll", "“y” as in yes", "/ʝ/", "llave"],
      ["ñ", "“ny” as in canyon", "/ɲ/", "niño"],
      ["rr", "strongly rolled", "/r/", "perro"],
      ["qu", "“k” (the u is silent)", "/k/", "queso"],
      ["gu + e/i", "hard “g” (the u is silent)", "/ɡ/", "guitarra"],
    ],
  },
];

function groupHTML(g) {
  const rows = g.rows
    .map(
      ([letter, sound, ipa, ex]) => `
      <div class="flex items-baseline gap-3 py-1.5 border-b border-dashed border-amber-300/50 last:border-0">
        <span class="font-bold ${g.color} text-xl min-w-[3.4rem]">${letter}</span>
        <span class="flex-1 text-sage-700">${sound}</span>
        <span class="text-xs text-sage-400 whitespace-nowrap" style="font-family:ui-monospace,monospace">${ipa}</span>
        <span class="font-semibold text-sage-800 min-w-[4.5rem] text-right">${ex}</span>${speakButtonHTML(ex)}
      </div>`
    )
    .join("");
  return `
    <div class="mb-6 last:mb-0">
      <h3 class="text-2xl ${g.color} sketch-underline inline-block mb-1">${g.title}</h3>
      <p class="text-sm text-sage-500 mb-2" style="font-family:Inter,sans-serif">${g.note}</p>
      <div>${rows}</div>
    </div>`;
}

export function soundMapView(app) {
  app.innerHTML = `
    <section class="max-w-3xl mx-auto fade-in">
      <div class="flex items-center gap-2 mb-1">
        <h2 class="text-4xl font-script text-sage-800">Sound Map</h2><span class="text-2xl">🔊</span>
      </div>
      <p class="text-sage-600 mb-4" style="font-family:Inter,sans-serif">Spanish is spelt as it sounds — learn these and you can pronounce almost any word on sight. Tap 🔊 to hear each example.</p>
      <div class="paper sketch-box p-5 sm:p-7 font-hand text-lg">
        ${GROUPS.map(groupHTML).join("")}
      </div>
      <p class="text-xs text-sage-400 mt-4" style="font-family:Inter,sans-serif">The “th” sound for <b>c</b> and <b>z</b> is standard in most of Spain; across Latin America it's an “s”. Both are correct.</p>
    </section>`;
  window.scrollTo({ top: 0, behavior: "smooth" });
}
